import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, Wrench, Lock } from "lucide-react";

export default function Maintenance({ info = {} }) {
  const title = (info.maintenance_title && info.maintenance_title.trim()) || "Vi pudser på hjemmesiden.";
  const message = (info.maintenance_message && info.maintenance_message.trim())
    || "Vi er i fuld gang med at gøre mahasa.dk endnu skarpere. Siden er tilbage meget snart — i mellemtiden er du altid velkommen til at kontakte os direkte.";
  const email = info.contact_email;
  const phone = info.contact_phone;

  return (
    <div className="min-h-screen bg-graphite text-white relative overflow-hidden" data-testid="maintenance-page">
      {/* Discreet admin login — top right corner */}
      <Link
        to="/admin"
        className="absolute top-5 right-5 sm:top-7 sm:right-7 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-white/55 hover:text-white hover:border-white/40 text-[11px] uppercase tracking-[0.18em] backdrop-blur-sm transition-colors"
        data-testid="maintenance-admin-login"
      >
        <Lock size={11} strokeWidth={1.8}/> Admin
      </Link>

      {/* Subtle ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-copper/10 blur-3xl"/>
        <div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full bg-amber-500/5 blur-3xl"/>
      </div>

      <div className="relative max-w-4xl mx-auto px-6 sm:px-8 py-20 sm:py-28 flex flex-col items-start min-h-screen justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full"
        >
          <img src="/brand/mahasa-logo.png" alt="Mahasa" className="h-12 sm:h-14 w-auto logo-on-dark mb-12"/>

          <div className="inline-flex items-center gap-2 text-copper text-[11px] uppercase tracking-[0.25em] mb-6">
            <Wrench size={14} strokeWidth={1.6}/> Under arbejde
          </div>

          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight mb-8" data-testid="maintenance-title">
            {title}
          </h1>

          <p className="font-body text-lg sm:text-xl text-white/70 leading-relaxed max-w-2xl mb-12 whitespace-pre-line" data-testid="maintenance-message">
            {message}
          </p>

          {(email || phone) && (
            <div className="border-t border-white/10 pt-8 space-y-3" data-testid="maintenance-contact">
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">Kontakt os direkte</div>
              {email && (
                <a href={`mailto:${email}`} className="group flex items-center gap-3 text-white/85 hover:text-copper transition-colors" data-testid="maintenance-email">
                  <Mail size={18} strokeWidth={1.5}/>
                  <span className="text-lg">{email}</span>
                </a>
              )}
              {phone && (
                <a href={`tel:${phone}`} className="group flex items-center gap-3 text-white/85 hover:text-copper transition-colors" data-testid="maintenance-phone">
                  <Phone size={18} strokeWidth={1.5}/>
                  <span className="text-lg">{phone}</span>
                </a>
              )}
            </div>
          )}
        </motion.div>

        <div className="absolute bottom-6 left-0 right-0 text-center text-[11px] tracking-widest text-white/30 uppercase">
          © {new Date().getFullYear()} Mahasa
        </div>
      </div>
    </div>
  );
}
